import type { ControlMode, InputFrame, InputAdapter, ControlAction, UiAction } from './types';
import { isChord, mapKeys } from './keymap';
import { movementPreferences } from './movementBindings';
import type { TouchInput } from './touchInput';
import {
  IDLE_DRAG_LOOK,
  pressDragLook,
  moveDragLook,
  releaseDragLook,
  cancelDragLook,
  type DragLookState,
} from './dragLook';

/* 'refused' is kept separate from 'unlocked' so the HUD can tell the user their click did not
   capture the mouse (Chrome refuses during the cooldown after Esc, in headless mode, or when the
   document is not focused) and point them at the drag fallback instead. */
export type PointerLockStatus = 'unlocked' | 'locked' | 'refused';

export interface DomInputAdapter extends InputAdapter {
  readonly pointerLockStatus: PointerLockStatus;
}

interface InputContext {
  mode?: () => ControlMode;
  thirdPerson?: () => boolean;
  touch?: TouchInput;
}

const ACTION_KEYS: ReadonlyMap<string, ControlAction> = new Map([
  ['KeyF', 'toggleMode'],
  ['KeyB', 'toggleBoat'],
  ['KeyP', 'painterView'],
]);

const UI_KEYS: ReadonlyMap<string, UiAction> = new Map([
  ['KeyH', 'help'],
  ['KeyO', 'overlay'],
  ['KeyG', 'quality'],
  ['KeyM', 'minimap'],
  ['KeyL', 'coords'],
]);

export interface InputState {
  pressed: Set<string>;
  lookDx: number;
  lookDy: number;
  orbitDx: number;
  orbitDy: number;
  mouseButtons: number;
  wheelSteps: number;
  pendingActions: ControlAction[];
  pendingUi: UiAction[];
  enabled: boolean;
  pointerLock: PointerLockStatus;
  discardLockMove: boolean;
  drag: DragLookState;
}

export function createInitialState(): InputState {
  return {
    pressed: new Set(),
    lookDx: 0,
    lookDy: 0,
    orbitDx: 0,
    orbitDy: 0,
    mouseButtons: 0,
    wheelSteps: 0,
    pendingActions: [],
    pendingUi: [],
    enabled: true,
    pointerLock: 'unlocked',
    discardLockMove: false,
    drag: IDLE_DRAG_LOOK,
  };
}

function resetInputState(state: InputState): void {
  state.pressed.clear();
  state.lookDx = 0;
  state.lookDy = 0;
  state.orbitDx = 0;
  state.orbitDy = 0;
  state.mouseButtons = 0;
  state.wheelSteps = 0;
  state.pendingActions = [];
  state.pendingUi = [];
  state.drag = IDLE_DRAG_LOOK;
}

function createPointerLock(state: InputState, target: HTMLElement, doc: Document) {
  function markRefused(): void {
    state.pointerLock = 'refused';
  }

  /* Chrome rejects the returned promise on refusal, which would surface as an unhandled
     rejection; Firefox and Safari return undefined and report only through pointerlockerror.
     Some embedded browsers throw synchronously instead of returning a promise at all, so the
     call itself needs a try/catch, not just a .catch() on its result. */
  function request(): void {
    try {
      void Promise.resolve(target.requestPointerLock()).catch(markRefused);
    } catch {
      markRefused();
    }
  }

  function onChange(): void {
    state.pointerLock = doc.pointerLockElement === target ? 'locked' : 'unlocked';
    // Pointer capture can recenter the cursor on entry. Its first relative delta is not a
    // deliberate look gesture, and can be very large; start with the next mouse movement.
    state.discardLockMove = state.pointerLock === 'locked';
    state.lookDx = 0;
    state.lookDy = 0;
    state.drag = IDLE_DRAG_LOOK;
  }

  function release(): void {
    if (doc.pointerLockElement === target) doc.exitPointerLock();
  }

  return { request, onChange, onError: markRefused, release };
}

export function createEventHandlers(state: InputState) {
  function onKeyDown(e: KeyboardEvent): void {
    if (!state.enabled) return;
    if (isChord(e)) {
      // macOS sends no keyup for a letter let go while Command is down: a held key would stick.
      state.pressed.clear();
      return;
    }
    state.pressed.add(e.code);
    if (
      Object.values(movementPreferences().bindings).includes(e.code) ||
      e.code.startsWith('Arrow')
    )
      e.preventDefault();

    const controlAction = ACTION_KEYS.get(e.code);
    if (controlAction) {
      state.pendingActions.push(controlAction);
      e.preventDefault();
    }

    const uiAction = UI_KEYS.get(e.code);
    if (uiAction && !(uiAction === 'minimap' && e.repeat)) {
      state.pendingUi.push(uiAction);
      e.preventDefault();
    }
  }

  function onKeyUp(e: KeyboardEvent): void {
    state.pressed.delete(e.code);
  }

  function onMouseMove(e: MouseEvent): void {
    if (!state.enabled || state.pointerLock !== 'locked') return;
    if (state.discardLockMove) {
      state.discardLockMove = false;
      return;
    }
    state.lookDx += e.movementX;
    state.lookDy += e.movementY;
  }

  function onWheel(e: WheelEvent): void {
    if (!state.enabled) return;
    state.wheelSteps += Math.sign(e.deltaY) * -1;
    e.preventDefault();
  }

  function onBlur(): void {
    resetInputState(state);
  }

  return { onKeyDown, onKeyUp, onMouseMove, onWheel, onBlur };
}

/* Drag-to-look is the fallback for browsers where requestPointerLock never resolves, rejects or
   fires pointerlockerror (so waiting on lock state alone can leave a user with no way to look
   around at all), and it also replaces the plain click listener: a press-release with little
   movement is the same "click requests lock" gesture as before, now decided by dragLook.ts
   instead of racing a separate click handler against it. */
function collectDragLook(state: InputState, dx: number, dy: number, orbit: boolean): void {
  if (orbit) {
    state.orbitDx += dx;
    state.orbitDy += dy;
  } else {
    state.lookDx += dx;
    state.lookDy += dy;
  }
}

function createDragHandlers(
  state: InputState,
  requestLock: () => void,
  target: HTMLElement,
  thirdPerson: () => boolean
) {
  function onPointerDown(e: PointerEvent): void {
    if (!state.enabled || (e.button !== 0 && e.button !== 2)) return;
    if (state.drag.phase === 'active' && state.drag.pointerId !== e.pointerId) return;
    state.mouseButtons = e.pointerType === 'touch' ? 0 : e.buttons;
    const locked = state.pointerLock === 'locked';
    state.drag = pressDragLook(
      state.drag,
      e.pointerId,
      e.clientX,
      e.clientY,
      state.enabled,
      locked
    );
    if (state.drag.phase !== 'active') return;
    target.setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  /* setPointerCapture keeps these events coming to the canvas for this pointerId even once the
     pointer wanders off it; clientX/Y differences stand in for movementX/Y, which pointer
     capture makes unreliable in some browsers. */
  function onPointerMove(e: PointerEvent): void {
    if (!state.enabled) return;
    const locked = state.pointerLock === 'locked';
    const result = moveDragLook(state.drag, e.pointerId, e.clientX, e.clientY, locked);
    state.drag = result.state;
    collectDragLook(
      state,
      result.dx,
      result.dy,
      e.pointerType !== 'touch' && thirdPerson() && !(state.mouseButtons & 2)
    );
  }

  function onPointerUp(e: PointerEvent): void {
    state.mouseButtons = e.pointerType === 'touch' ? 0 : e.buttons;
    if (e.buttons !== 0) return;
    const result = releaseDragLook(state.drag, e.pointerId);
    state.drag = result.state;
    if (
      state.enabled &&
      !e.defaultPrevented &&
      e.pointerType !== 'touch' &&
      !thirdPerson() &&
      e.button === 0 &&
      !result.wasDrag &&
      state.pointerLock !== 'locked'
    )
      requestLock();
  }

  function onPointerCancel(e: PointerEvent): void {
    state.drag = cancelDragLook(state.drag, e.pointerId);
    state.mouseButtons = 0;
  }

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}

type Handlers = ReturnType<typeof createEventHandlers>;
type DragHandlers = ReturnType<typeof createDragHandlers>;
type PointerLock = ReturnType<typeof createPointerLock>;

function bindListeners(
  target: HTMLElement,
  doc: Document,
  h: Handlers,
  drag: DragHandlers,
  lock: PointerLock
) {
  doc.addEventListener('keydown', h.onKeyDown);
  doc.addEventListener('keyup', h.onKeyUp);
  doc.addEventListener('mousemove', h.onMouseMove);
  target.addEventListener('wheel', h.onWheel, { passive: false });
  target.addEventListener('pointerdown', drag.onPointerDown);
  target.addEventListener('pointermove', drag.onPointerMove);
  target.addEventListener('pointerup', drag.onPointerUp);
  target.addEventListener('pointercancel', drag.onPointerCancel);
  target.addEventListener('lostpointercapture', drag.onPointerCancel);
  doc.addEventListener('pointerlockchange', lock.onChange);
  doc.addEventListener('pointerlockerror', lock.onError);

  return function unbind(): void {
    doc.removeEventListener('keydown', h.onKeyDown);
    doc.removeEventListener('keyup', h.onKeyUp);
    doc.removeEventListener('mousemove', h.onMouseMove);
    target.removeEventListener('wheel', h.onWheel);
    target.removeEventListener('pointerdown', drag.onPointerDown);
    target.removeEventListener('pointermove', drag.onPointerMove);
    target.removeEventListener('pointerup', drag.onPointerUp);
    target.removeEventListener('pointercancel', drag.onPointerCancel);
    target.removeEventListener('lostpointercapture', drag.onPointerCancel);
    doc.removeEventListener('pointerlockchange', lock.onChange);
    doc.removeEventListener('pointerlockerror', lock.onError);
  };
}

function bindSuspension(state: InputState, doc: Document, touch?: TouchInput): () => void {
  const clear = (): void => {
    resetInputState(state);
    touch?.clear();
  };
  const visibility = (): void => {
    if (doc.hidden) clear();
  };
  window.addEventListener('blur', clear);
  window.addEventListener('orientationchange', clear);
  doc.addEventListener('visibilitychange', visibility);
  return () => {
    window.removeEventListener('blur', clear);
    window.removeEventListener('orientationchange', clear);
    doc.removeEventListener('visibilitychange', visibility);
  };
}

function pollInput(state: InputState, context: InputContext) {
  const touch = context.touch?.poll();
  const mouseSteer = Boolean(state.mouseButtons & 2) || state.pointerLock === 'locked';
  const movement = mapKeys(state.pressed, {
    steering: mouseSteer,
    mode: context.mode?.() ?? 'walk',
    preferences: movementPreferences(),
  });
  const frame: InputFrame = {
    ...movement,
    forward: Math.max(
      -1,
      Math.min(1, ((state.mouseButtons & 3) === 3 ? 1 : movement.forward) + (touch?.forward ?? 0))
    ),
    right: Math.max(-1, Math.min(1, movement.right + (touch?.right ?? 0))),
    up: Math.max(-1, Math.min(1, movement.up + (touch?.up ?? 0))),
    boost: movement.boost || Boolean(touch?.boost),
    lookDx: state.lookDx,
    lookDy: state.lookDy,
    orbitDx: state.orbitDx,
    orbitDy: state.orbitDy,
    mouseSteer,
    mouseOrbit: Boolean(state.mouseButtons & 1) && !mouseSteer,
    wheelSteps: state.wheelSteps,
    actions: [...state.pendingActions, ...(touch?.actions ?? [])],
  };
  const ui = state.pendingUi;
  state.lookDx = 0;
  state.lookDy = 0;
  state.orbitDx = 0;
  state.orbitDy = 0;
  state.wheelSteps = 0;
  state.pendingActions = [];
  state.pendingUi = [];
  return { frame, ui };
}

export function createInputAdapter(
  target: HTMLElement,
  doc: Document,
  context: InputContext = {}
): DomInputAdapter {
  const state = createInitialState();
  const lock = createPointerLock(state, target, doc);
  const handlers = createEventHandlers(state);
  const dragHandlers = createDragHandlers(
    state,
    lock.request,
    target,
    context.thirdPerson ?? (() => false)
  );
  const unbind = bindListeners(target, doc, handlers, dragHandlers, lock);
  const unbindSuspension = bindSuspension(state, doc, context.touch);

  return {
    poll: () => pollInput(state, context),

    /* A disabled adapter must not keep the pointer captive: the open modal needs a visible
       cursor so it can be dismissed by clicking outside its content. */
    setEnabled(value: boolean): void {
      state.enabled = value;
      context.touch?.setEnabled(value);
      if (!value) {
        resetInputState(state);
        lock.release();
      }
    },

    get pointerLocked(): boolean {
      return state.pointerLock === 'locked';
    },

    get pointerLockStatus(): PointerLockStatus {
      return state.pointerLock;
    },

    requestPointerLock(): void {
      lock.request();
    },

    dispose(): void {
      unbind();
      unbindSuspension();
      context.touch?.clear();
    },
  };
}
